import React, { useState } from "react";
import { Star, MessageCircle, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface Comment {
  id: string | number;
  user: string;
  avatar?: string;
  rating: number;
  comment: string;
  timestamp: string;
  likes: number;
}

interface CommentSectionProps {
  itemId: string;
  itemType: 'workflow' | 'flow' | 'integration';
  comments: Comment[];
  onAddComment?: (comment: { rating: number; text: string }) => void;
  accentColor?: string; // e.g., 'blue', 'green', 'purple'
}

export const CommentSection: React.FC<CommentSectionProps> = ({
  itemId,
  itemType,
  comments = [],
  onAddComment,
  accentColor = 'blue'
}) => {
  const [newComment, setNewComment] = useState('');
  const [userRating, setUserRating] = useState(0);

  const colorClasses = {
    blue: {
      icon: 'text-blue-600',
      button: 'bg-blue-600 hover:bg-blue-700',
      focus: 'focus:ring-blue-500',
      hover: 'hover:text-blue-600'
    },
    green: {
      icon: 'text-green-600', 
      button: 'bg-green-600 hover:bg-green-700',
      focus: 'focus:ring-green-500',
      hover: 'hover:text-green-600'
    },
    purple: {
      icon: 'text-purple-600',
      button: 'bg-purple-600 hover:bg-purple-700', 
      focus: 'focus:ring-purple-500',
      hover: 'hover:text-purple-600'
    }
  };

  const colors = colorClasses[accentColor as keyof typeof colorClasses] || colorClasses.blue;

  const handleSubmitComment = () => {
    if (onAddComment && newComment.trim() && userRating > 0) {
      onAddComment({
        rating: userRating,
        text: newComment.trim()
      });
      setNewComment('');
      setUserRating(0);
    }
  };

  const renderStarRating = (rating: number, interactive = false, onRatingChange?: (rating: number) => void) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
            } ${interactive ? `cursor-pointer ${colors.hover} transition-colors` : ""}`}
            onClick={interactive && onRatingChange ? () => onRatingChange(star) : undefined}
          />
        ))}
      </div>
    );
  };

  return (
    <section id="comments-section">
      <h2 className={`text-3xl font-bold text-slate-900 dark:text-white mb-8 flex items-center`}>
        <MessageCircle className={`h-8 w-8 mr-3 ${colors.icon}`} />
        Comments & Reviews
      </h2>
      
      {/* Add Comment Form */}
      <Card className="bg-white dark:bg-slate-800 mb-6">
        <CardHeader>
          <CardTitle className="text-xl text-slate-900 dark:text-white">Share Your Experience</CardTitle>
          <CardDescription>Let the community know what you think of this {itemType}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Your Rating</label>
            {renderStarRating(userRating, true, setUserRating)}
          </div>
          <div>
            <label htmlFor="comment" className="text-sm font-medium mb-2 block">Your Comment</label>
            <textarea
              id="comment"
              rows={4}
              className={`w-full p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white resize-none ${colors.focus} focus:border-transparent`}
              placeholder={`Share your experience with this ${itemType}...`}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
          </div>
          <Button 
            className={colors.button}
            onClick={handleSubmitComment}
            disabled={!newComment.trim() || userRating === 0}
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Post Comment
          </Button>
        </CardContent>
      </Card>

      {/* Comments List */}
      <Card className="bg-white dark:bg-slate-800">
        <CardHeader>
          <CardTitle className="text-xl text-slate-900 dark:text-white">Community Feedback</CardTitle>
          <CardDescription>{comments.length} comments from the community</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {comments.length > 0 ? (
            comments.map((comment) => (
              <div key={comment.id} className="border-b border-slate-200 dark:border-slate-700 last:border-b-0 pb-6 last:pb-0">
                <div className="flex items-start space-x-4">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={comment.avatar} alt={comment.user} />
                    <AvatarFallback>
                      {comment.user.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-medium text-slate-900 dark:text-white">{comment.user}</h4>
                      {renderStarRating(comment.rating)}
                      <span className="text-sm text-slate-500 dark:text-slate-400">{comment.timestamp}</span>
                    </div>
                    <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mb-3">
                      {comment.comment}
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-slate-500 dark:text-slate-400">
                      <button className={`flex items-center space-x-1 ${colors.hover} transition-colors`}>
                        <Heart className="h-4 w-4" />
                        <span>{comment.likes}</span>
                      </button>
                      <button className={`${colors.hover} transition-colors`}>
                        Reply
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <MessageCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium mb-2">No comments yet</p>
              <p>Be the first to share your experience with this {itemType}!</p>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
};